import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Button } from '../components/ui/button';
import { 
  Scale, Shield, Clock, CheckCircle2, Award, Star, 
  Users, Menu, X, ArrowRight, Mail, Phone, Sparkles,
  Zap, TrendingUp, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import { auth } from '../utils/auth';
import HeroMock from '../components/landing/HeroMock';
import LivingStructureBackground from '../components/landing/LivingStructureBackground';

const LandingWorldClass = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleGetStarted = () => {
    const user = auth.getUser();
    
    if (user) {
      if (user.role === 'LAWYER' || user.role === 'ADMIN' || user.role === 'TENANT_ADMIN') {
        auth.logout();
        toast.success('Du er nå logget ut fra Lexy. Start din kunde-sak!', { duration: 4000 });
        window.location.href = '/chat';
        return;
      }
      navigate('/cases');
    } else {
      navigate('/chat');
    }
  };

  const quickLogin = async (email, password) => {
    try {
      const response = await auth.login(email, password);
      const userRole = response.role;
      
      if (userRole === 'LAWYER') navigate('/lexy/dashboard');
      else if (userRole === 'ADMIN') navigate('/admin');
      else navigate('/cases');
    } catch (error) {
      toast.error('Kunne ikke logge inn');
    }
  };

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      
      {/* Living Structure Background - Animated organic pattern */}
      <LivingStructureBackground />

      {/* Ultra-modern Header with glassmorphism */}
      <motion.header 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/50 shadow-sm"
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Premium Logo */}
            <motion.div 
              className="flex items-center gap-3 cursor-pointer group"
              onClick={() => navigate('/')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="w-11 h-11 bg-slate-900 rounded-xl flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
                <Scale className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-lg font-bold text-slate-900">
                  Advokat for alle
                </span>
                <div className="text-xs text-slate-500 font-medium">Premium advokatfirma</div>
              </div>
            </motion.div>

            {/* Modern Nav */}
            <nav className="hidden md:flex items-center gap-6">
              {['Tjenester', 'Priser', 'For advokater'].map((item, i) => (
                <motion.a
                  key={item}
                  href={i === 2 ? '/lexy' : `#${item.toLowerCase()}`}
                  className="relative text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors group"
                  whileHover={{ y: -1 }}
                >
                  {item}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-emerald-500 to-emerald-600 group-hover:w-full transition-all duration-300" />
                </motion.a>
              ))}
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  onClick={handleGetStarted}
                  className="relative bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 hover:from-slate-800 hover:via-slate-700 hover:to-slate-800 text-white font-medium shadow-lg shadow-slate-900/20 hover:shadow-xl hover:shadow-slate-900/30 transition-all"
                >
                  <span className="relative z-10">Kom i gang</span>
                  <ArrowRight className="w-4 h-4 ml-2 relative z-10" />
                </Button>
              </motion.div>
            </nav>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </motion.header>

      {/* HERO - Hypermodern Split with 3D effects */}
      <section className="relative pt-32 pb-24 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            
            {/* Left: Premium Content with animations */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 border border-slate-200 rounded-full mb-8 cursor-default"
              >
                <Sparkles className="w-4 h-4 text-slate-700" />
                <span className="text-sm font-medium text-slate-700">Norges mest moderne advokatfirma</span>
              </motion.div>

              {/* Hero headline with gradient */}
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-5xl md:text-6xl lg:text-7xl font-black mb-6 leading-[1.1]"
              >
                <span className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 bg-clip-text text-transparent">
                  Juridisk hjelp
                </span>
                <br />
                <span className="bg-gradient-to-r from-emerald-600 via-emerald-500 to-emerald-600 bg-clip-text text-transparent">
                  til en fast pris
                </span>
              </motion.h1>

              {/* Premium description */}
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-xl text-slate-600 mb-8 leading-relaxed"
              >
                Profesjonell juridisk vurdering fra erfarne advokater.
                <br />
                <span className="font-semibold text-slate-900">Fast pris på 499 kr per sak.</span>
              </motion.p>

              {/* Clean price display */}
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 }}
                className="relative inline-block mb-10"
              >
                <div className="bg-white border border-slate-200 rounded-2xl px-8 py-6 shadow-lg">
                  <div className="flex items-baseline gap-4">
                    <span className="text-6xl font-black text-slate-900">
                      499 kr
                    </span>
                    <div className="flex flex-col">
                      <span className="text-slate-400 line-through text-xl font-medium">4.990 kr</span>
                      <span className="text-emerald-600 text-sm font-bold">Spar 90%</span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Premium CTAs */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    onClick={handleGetStarted}
                    size="lg"
                    className="bg-slate-900 hover:bg-slate-800 text-white px-10 py-7 text-lg font-bold shadow-lg hover:shadow-xl transition-all"
                  >
                    <span className="flex items-center">
                      Start din sak
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </span>
                  </Button>
                </motion.div>
                
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    onClick={() => navigate('/venteliste')}
                    size="lg"
                    variant="outline"
                    className="px-10 py-7 text-lg font-bold border-2 border-slate-300 hover:border-slate-400 hover:bg-slate-50 transition-all"
                  >
                    Meld interesse
                  </Button>
                </motion.div>
              </motion.div>

              {/* Trust badges with icons */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="flex flex-wrap gap-6"
              >
                {[
                  { icon: CheckCircle2, text: 'Autoriserte advokater' },
                  { icon: Shield, text: 'GDPR-sikker' },
                  { icon: Clock, text: 'Svar innen 48t' }
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    className="flex items-center gap-2 group"
                    whileHover={{ x: 5 }}
                  >
                    <div className="p-1.5 bg-emerald-50 rounded-lg group-hover:bg-emerald-100 transition-colors">
                      <item.icon className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="text-sm font-medium text-slate-700">{item.text}</span>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            {/* Right: Product Preview - Real Components */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative lg:ml-8"
            >
              {/* Subtle ambient glow behind the mock */}
              <div className="absolute -inset-4 bg-gradient-to-br from-slate-100 via-transparent to-slate-100/50 rounded-3xl blur-2xl opacity-60" />
              
              {/* The Hero Mock - Real UI Components */}
              <div className="relative">
                <HeroMock />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats - Clean and professional */}
      <section className="py-20 px-6 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '1,200+', label: 'Klienter', icon: Users },
              { value: '3,500+', label: 'Saker løst', icon: CheckCircle2 },
              { value: '4.9', label: 'Vurdering', icon: Star },
              { value: '24t', label: 'Responstid', icon: Zap }
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center p-6"
              >
                <stat.icon className="w-8 h-8 text-slate-700 mx-auto mb-3" />
                <div className="text-5xl font-black text-slate-900 mb-2">
                  {stat.value}
                </div>
                <div className="text-sm font-medium text-slate-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works - Modern cards */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <span className="inline-block px-4 py-2 bg-slate-100 text-slate-700 rounded-full text-sm font-semibold mb-4">
              Slik fungerer det
            </span>
            <h2 className="text-5xl font-black text-slate-900 mb-4">
              Tre enkle steg
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Fra beskrivelse til juridisk vurdering på under 48 timer
            </p>
          </motion.div>

          <div className="max-w-5xl mx-auto space-y-8">
            {[
              {
                step: '1',
                title: 'Beskriv din sak',
                desc: 'Fortell oss om din juridiske situasjon. Du kan skrive fritt, vår AI hjelper deg strukturere informasjonen.',
                icon: Users
              },
              {
                step: '2',
                title: 'Advokat vurderer',
                desc: 'En autorisert advokat med relevant erfaring går gjennom saken din og utarbeider en profesjonell juridisk vurdering.',
                icon: Scale
              },
              {
                step: '3',
                title: 'Motta veiledning',
                desc: 'Du får skriftlig juridisk vurdering med konkrete anbefalinger innen 24-48 timer. Fast pris på 499 kr.',
                icon: CheckCircle2
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.2 }}
                whileHover={{ y: -2 }}
                className="flex gap-8 items-start p-8 bg-white border border-slate-200 rounded-3xl hover:border-slate-300 hover:shadow-lg transition-all"
              >
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 bg-slate-900 text-white rounded-2xl flex items-center justify-center shadow-lg">
                    <item.icon className="w-10 h-10" />
                  </div>
                </div>
                <div className="flex-1 pt-2">
                  <div className="text-sm font-bold text-slate-500 mb-2">STEG {item.step}</div>
                  <h3 className="text-3xl font-bold text-slate-900 mb-3">{item.title}</h3>
                  <p className="text-lg text-slate-600 leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services - Modern grid with hover effects */}
      <section id="tjenester" className="py-24 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <span className="inline-block px-4 py-2 bg-slate-100 text-slate-700 rounded-full text-sm font-semibold mb-4">
              Fagområder
            </span>
            <h2 className="text-5xl font-black text-slate-900 mb-4">
              Juridisk kompetanse
            </h2>
            <p className="text-xl text-slate-600">
              Ekspertise innen flere rettsområder
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Arbeidsrett', desc: 'Oppsigelse, diskriminering, arbeidsavtaler' },
              { title: 'Kontraktsrett', desc: 'Kontraktsbrudd, forhandlinger, tvister' },
              { title: 'Forbrukerrett', desc: 'Reklamasjon, mangel, kjøp og salg' },
              { title: 'Familierett', desc: 'Skilsmisse, samlivsbrudd, arv' },
              { title: 'Eiendomsrett', desc: 'Kjøp, salg, nabotvister' },
              { title: 'Selskapsrett', desc: 'Avtaler, stiftelse, aksjonærtvister' }
            ].map((service, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -3 }}
                className="bg-white rounded-2xl p-8 border border-slate-200 hover:border-slate-300 shadow-md hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center mb-4">
                  <Scale className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing - Premium card */}
      <section id="priser" className="py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-slate-100 text-slate-700 rounded-full text-sm font-semibold mb-4">
              Priser
            </span>
            <h2 className="text-5xl font-black text-slate-900 mb-4">
              Transparent prissetting
            </h2>
            <p className="text-xl text-slate-600">
              Fast pris per sak. Ingen skjulte kostnader.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="bg-white rounded-3xl p-12 border border-slate-200 shadow-xl">
              <div className="text-center mb-10">
                <div className="inline-flex items-baseline gap-4 mb-4">
                  <span className="text-8xl font-black text-slate-900">
                    499 kr
                  </span>
                </div>
                <div className="text-lg text-slate-600 mb-4 font-medium">
                  per juridisk vurdering
                </div>
                <div className="inline-flex items-center gap-4">
                  <span className="text-2xl text-slate-400 line-through font-medium">4.990 kr</span>
                  <span className="px-4 py-2 bg-emerald-600 text-white rounded-xl text-base font-bold">
                    Spar 90%
                  </span>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-10">
                {[
                  'Autorisert norsk advokat',
                  'Skriftlig juridisk vurdering',
                  'Konkrete anbefalinger',
                  'Svar innen 24-48 timer',
                  'Chat med advokat',
                  'GDPR-sikker behandling',
                  '14 dagers angrerett',
                  'Ingen binding'
                ].map((feature, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                    className="flex items-center gap-3"
                  >
                    <div className="p-1 bg-emerald-100 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    </div>
                    <span className="text-slate-700 font-medium">{feature}</span>
                  </motion.div>
                ))}
              </div>

              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  onClick={handleGetStarted}
                  size="lg"
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white py-8 text-xl font-bold shadow-lg hover:shadow-xl transition-all"
                >
                  Start din sak nå
                  <ArrowRight className="w-6 h-6 ml-3" />
                </Button>
              </motion.div>

              <p className="text-center text-sm text-slate-500 mt-6 font-medium">
                🔒 Sikker betaling • Ingen binding • 14 dagers angrerett
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials - Premium cards */}
      <section className="py-24 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <span className="inline-block px-4 py-2 bg-slate-100 text-slate-700 rounded-full text-sm font-semibold mb-4">
              Tilbakemeldinger
            </span>
            <h2 className="text-5xl font-black text-slate-900 mb-4">
              Hva klientene sier
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Kari Nordmann',
                role: 'Arbeidsrett',
                text: 'Profesjonell håndtering av min sak. Fikk tydelig juridisk vurdering som ga meg trygghet i neste steg.',
                rating: 5
              },
              {
                name: 'Thomas Jensen',
                role: 'Kontraktsrett',
                text: 'Rask og grundig behandling. God veiledning som hjalp oss i forhandlinger med leverandør.',
                rating: 5
              },
              {
                name: 'Linda Svendsen',
                role: 'Forbrukerrett',
                text: 'Enkelt å bruke. God kommunikasjon. Fikk konkret hjelp til min reklamasjonssak.',
                rating: 5
              }
            ].map((testimonial, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -3 }}
                className="bg-white rounded-2xl p-8 border border-slate-200 hover:border-slate-300 shadow-md hover:shadow-lg transition-all"
              >
                <div className="flex gap-1 mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-slate-900 text-slate-900" />
                  ))}
                </div>
                
                <p className="text-slate-700 mb-8 text-lg leading-relaxed italic">
                  "{testimonial.text}"
                </p>
                
                <div className="flex items-center gap-4 pt-6 border-t border-slate-200">
                  <div className="w-12 h-12 bg-slate-900 rounded-full flex items-center justify-center font-bold text-white text-lg">
                    {testimonial.name[0]}
                  </div>
                  <div>
                    <div className="font-bold text-slate-900">{testimonial.name}</div>
                    <div className="text-sm text-slate-600">{testimonial.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust - Clean badges */}
      <section className="py-20 px-6 bg-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12">
            {[
              {
                icon: Shield,
                title: 'Taushetsplikt',
                desc: 'All kommunikasjon er konfidensiell'
              },
              {
                icon: Award,
                title: 'Autorisert',
                desc: 'Medlem av Advokatforeningen'
              },
              {
                icon: CheckCircle2,
                title: 'Forsikret',
                desc: 'Profesjonsforsikring tegnet'
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center shadow-lg mx-auto mb-6">
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-bold text-xl text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA - Clean dark section */}
      <section className="relative py-32 px-6 bg-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-5xl md:text-6xl font-black text-white mb-8 leading-tight">
              Trenger du juridisk hjelp?
            </h2>
            <p className="text-2xl text-slate-400 mb-12 leading-relaxed max-w-2xl mx-auto">
              Start din sak i dag. Fast pris. Profesjonell vurdering.
            </p>
            
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button
                onClick={handleGetStarted}
                size="lg"
                className="bg-white hover:bg-slate-50 text-slate-900 px-16 py-8 text-2xl font-bold shadow-xl hover:shadow-2xl transition-all"
              >
                Start din sak nå
                <ArrowRight className="w-7 h-7 ml-3" />
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer - Clean */}
      <footer className="py-16 px-6 bg-slate-950 text-slate-400 border-t border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center">
                  <Scale className="w-6 h-6 text-slate-900" />
                </div>
                <span className="font-bold text-white text-lg">Advokat for alle</span>
              </div>
              <p className="text-sm text-slate-500 mb-3">
                Premium advokatfirma
              </p>
              <p className="text-xs text-slate-600">
                Org.nr: 123 456 789
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-4">Tjenester</h3>
              <ul className="space-y-3 text-sm">
                <li><a href="#tjenester" className="hover:text-emerald-400 transition-colors">Arbeidsrett</a></li>
                <li><a href="#tjenester" className="hover:text-emerald-400 transition-colors">Kontraktsrett</a></li>
                <li><a href="#tjenester" className="hover:text-emerald-400 transition-colors">Forbrukerrett</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-4">Informasjon</h3>
              <ul className="space-y-3 text-sm">
                <li><a href="/privacy" className="hover:text-emerald-400 transition-colors">Personvern</a></li>
                <li><a href="/terms" className="hover:text-emerald-400 transition-colors">Vilkår</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-4">Kontakt</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-400" />
                  post@advokatforalle.no
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-slate-400" />
                  +47 22 00 00 00
                </li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-slate-800 text-center text-sm text-slate-500">
            <p>© 2025 Advokat for alle AS • Medlem av Advokatforeningen</p>
          </div>
        </div>
      </footer>

      {/* Dev Quick Login */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed bottom-4 right-4 space-y-2 z-50">
          <Button
            onClick={() => quickLogin('klient@example.com', 'Password123!')}
            size="sm"
            className="bg-white shadow-xl block"
          >
            🧪 Klient
          </Button>
          <Button
            onClick={() => quickLogin('advokat@example.com', 'Password123!')}
            size="sm"
            className="bg-white shadow-xl block"
          >
            🧪 Advokat
          </Button>
        </div>
      )}
    </div>
  );
};

export default LandingWorldClass;
